import { createRouter, createWebHistory } from 'vue-router';
import homePage from './views/homePage.vue';
import chooseProducts from './views/chooseProducts.vue';
import quoteForm from './components/forms/QuoteForm.vue';
import StandardQuoteForm from './components/forms/StandardQuoteForm.vue';
import InsuranceProductPage from './views/InsuranceProductPage.vue';
import PetInsurancePage from './views/products/PetInsurancePage.vue';
import AboutUs from './components/AboutUs.vue';
import customerServicePage from './views/customerServicePage.vue';
import citizensFormUpload from './components/forms/CitizensFormUpload.vue';
import PolicySearchComponent from './components/customerservice/policy-management/search/PolicySearchComponent.vue';
import RequestProofOfInsurance from './components/customerservice/requestProofOfInsurance.vue';
import CarrierDirectoryComponent from './components/customerservice/policy-management/directory/CarrierDirectoryComponent.vue';
import DocumentManagementComponent from './components/customerservice/policy-management/documents/DocumentManagementComponent.vue';
import store from './store/index.js';  // Assuming the store setup is correct and includes necessary getters
import GuardianAngelQuoteForm from './components/forms/GuardianAngelQuoteForm.vue';
import GuardianAngelQuoteFormStandardized from './components/forms/GuardianAngelQuoteFormStandardized.vue';
import CarrierServicesPage from './components/CarrierServicesPage.vue';
import NotFoundPage from './views/NotFoundPage.vue';

// Customer Service Components
import CitizensQandA from './components/customerservice/CitizensQandA.vue';
import serviceRequests from './components/customerservice/serviceRequests.vue';
import LenderRequestNavigation from './components/customerservice/policy-management/navigation/LenderRequestNavigation.vue';
import MortgageeChangeForm from './components/customerservice/policy-management/forms/mortgagee/MortgageeChangeForm.vue';
import HomePolicyChangeForm from './components/customerservice/policy-management/forms/home/HomePolicyChangeForm.vue';
import CancellationRequestForm from './components/customerservice/policy-management/forms/cancellation/CancellationRequestForm.vue';
import AutoPolicyChangeForm from './components/customerservice/policy-management/forms/auto/AutoPolicyChangeForm.vue';
import PolicyOtherChangeForm from './components/customerservice/policy-management/forms/other/PolicyOtherChangeForm.vue';

const routes = [
  { 
    path: '/embed/guardian-angel-quote-form', 
    component: GuardianAngelQuoteForm, 
    name: 'guardianAngelQuoteEmbed',
    props: { isEmbedded: true }
  },
  { 
    path: '/embed/guardian-angel-quote-form/standardized', 
    component: GuardianAngelQuoteFormStandardized, 
    name: 'guardianAngelQuoteStandardizedEmbed',
    props: { isEmbedded: true }
  },
  { path: '/guardian-angel-quote-form', component: GuardianAngelQuoteForm, name: 'guardianAngelQuote', meta: { hideChatwoot: true } },
  { path: '/guardian-angel-quote-form/standardized', component: GuardianAngelQuoteFormStandardized, name: 'guardianAngelQuoteStandardized', meta: { hideChatwoot: true } },
  { path: '/', component: homePage, name: 'home' },
  { path: '/chooseproducts', component: chooseProducts, name: 'chooseProducts' },
  { path: '/quotes/:id', component: quoteForm, props: true, name: 'quoteForm' },
  { path: '/quotes/standard/:productType', component: StandardQuoteForm, props: true, name: 'standardQuoteForm' },
  { path: '/aboutus', component: AboutUs, name: 'aboutUs' },
  { path: '/products/pet-insurance', component: PetInsurancePage, name: 'petInsurancePage' },
  { path: '/products/:productType', component: InsuranceProductPage, name: 'InsuranceProductPage' },
  { path: '/carrier-services', component: CarrierServicesPage, name: 'carrierServices' },
  {
    path: '/customerservice', 
    component: customerServicePage, 
    name: 'customerServicePage', // Parent route remains
  },
  // --- Flattened Customer Service Routes ---
  {
    path: '/customerservice/citizens', 
    name: 'customerService.citizens', // Keep name for consistency if needed
    component: CitizensQandA, 
  },
  {
    // Route for the citizens upload form
    path: '/customerservice/citizens-upload/:formType', 
    component: citizensFormUpload, 
    name: 'customerService.citizens.upload' 
  },
  {
    path: '/customerservice/policy-changes',
    name: 'customerService.policyChanges',
    component: serviceRequests, // Shows the policy change options
  },
  // Specific Policy Change Forms (now top-level relative to /customerservice)
  { 
    path: '/customerservice/policy-changes/mortgagee', 
    name: 'customerService.policyChanges.mortgagee', 
    component: MortgageeChangeForm // Use the correct form component
  },
  { 
    path: '/customerservice/policy-changes/home', 
    name: 'customerService.policyChanges.home', 
    component: HomePolicyChangeForm 
  },
  { 
    path: '/customerservice/policy-changes/cancellation', 
    name: 'customerService.policyChanges.cancellation', 
    component: CancellationRequestForm 
  },
  { 
    path: '/customerservice/policy-changes/auto', 
    name: 'customerService.policyChanges.auto', 
    component: AutoPolicyChangeForm 
  },
  { 
    path: '/customerservice/policy-changes/other', 
    name: 'customerService.policyChanges.other', 
    component: PolicyOtherChangeForm 
  },
  {
    path: '/customerservice/lender-requests',
    name: 'customerService.lenderRequests',
    component: LenderRequestNavigation, // Updated to use the new navigation component
  },
  // Specific Lender Request Forms/Actions (now top-level relative to /customerservice)
  { 
    path: '/customerservice/lender-requests/mortgagee', 
    name: 'customerService.lenderRequests.mortgagee', 
    component: MortgageeChangeForm, // Re-use the correct mortgagee form component
    props: { customerType: 'lender' }
  }, 
  { 
    path: '/customerservice/lender-requests/coverage', 
    name: 'customerService.lenderRequests.coverage', 
    component: HomePolicyChangeForm, 
    props: { customerType: 'lender' } 
  },
  { 
    path: '/customerservice/lender-requests/proof-of-insurance', 
    name: 'customerService.lenderRequests.proof', 
    component: RequestProofOfInsurance 
  },
  // --- End Flattened Routes ---
  
  // DEPRECATED: Old policy search route - redirecting to new component
  {
    path: '/policy-search',
    redirect: to => {
      return { 
        name: 'customerService.policySearch',
        query: to.query 
      };
    }
  },
  
  // New policy search route - consolidate to avoid duplication
  { 
    path: '/customerservice/policy-search', 
    component: PolicySearchComponent, 
    name: 'customerService.policySearch'
  },
  
  // DEPRECATED: SimpleAutoChangesForm - Redirecting to the modern AutoPolicyChangeForm
  // This redirect preserves backward compatibility with existing links while consolidating functionality
  {
    path: '/customerservice/policy-changes/simple-auto',
    redirect: to => {
      // Preserve any query parameters when redirecting
      return { 
        path: '/customerservice/policy-changes/auto',
        query: to.query 
      }
    }
  },
  
  // DEPRECATED: Old carrier directory route - redirecting to new component
  // Use a unique parameter name to avoid duplication
  {
    path: '/customerservice/carrier-directory/:carrierType',
    redirect: to => {
      // Convert old parameter formats to new format
      const directoryType = to.params.carrierType === 'make-claim' ? 'claim' : 'payment';
      return { 
        name: 'customerService.carrierDirectory',
        params: { directoryType },
        query: to.query 
      };
    }
  },
  
  // New carrier directory route with a different parameter name
  { 
    path: '/customerservice/carrier-directory/:directoryType', 
    component: CarrierDirectoryComponent, 
    name: 'customerService.carrierDirectory',
    props: route => ({
      initialType: route.params.directoryType === 'claim' ? 'claim' : 'payment',
      customerType: route.query.customerType || 'customer'
    })
  },

  // Consolidate document management routes into a single route with redirect
  { 
    path: '/customerservice/document-management', 
    component: DocumentManagementComponent, 
    name: 'customerService.documentManagement'
  },
  
  // DEPRECATED: Old otherPolicyChanges route - redirecting to the modern PolicyOtherChangeForm
  {
    path: '/customerservice/policy-changes/other-changes',
    redirect: to => {
      return {
        name: 'customerService.policyChanges.other',
        query: to.query
      }
    }
  },
  
  // DEPRECATED: Old mortgageeChange route - already handled by the MortgageeChangeForm
  // This redirect ensures any old links still work
  {
    path: '/customerservice/mortgagee-change',
    redirect: to => {
      return {
        name: 'customerService.policyChanges.mortgagee',
        query: to.query
      }
    }
  },
  
  // Campaign quote routes - handle /quotes/{campaign}/{productType}
  { 
    path: '/quotes/:campaign/:productType', 
    component: StandardQuoteForm, 
    props: route => ({
      productType: route.params.productType.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()).replace(' Insurance', ' Insurance'),
      campaign: route.params.campaign,
      utm_source: route.query.utm_source,
      utm_medium: route.query.utm_medium,
      utm_campaign: route.query.utm_campaign,
      utm_content: route.query.utm_content,
      utm_term: route.query.utm_term
    }),
    name: 'campaignQuoteForm',
    beforeEnter: (to, from, next) => {
      // Valid campaigns and product types
      const validCampaigns = ['quote-slash', 'special-offer', 'tm6c14']; // Add more as needed
      const validProductTypes = ['home-insurance', 'auto-insurance', 'flood-insurance', 'life-insurance', 'medicare', 'commercial-insurance', 'recreational-insurance', 'umbrella-insurance'];
      
      if (!validCampaigns.includes(to.params.campaign) || !validProductTypes.includes(to.params.productType)) {
        next({ name: 'notFound' });
      } else {
        next();
      }
    }
  },
  
  // Standard quote routes - handle /quotes/{productType}
  { 
    path: '/quotes/:productType', 
    component: StandardQuoteForm, 
    props: route => ({
      productType: route.params.productType.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()).replace(' Insurance', ' Insurance'),
      utm_source: route.query.utm_source,
      utm_medium: route.query.utm_medium,
      utm_campaign: route.query.utm_campaign,
      utm_content: route.query.utm_content,
      utm_term: route.query.utm_term
    }),
    name: 'standardProductQuoteForm',
    beforeEnter: (to, from, next) => {
      // Valid product types
      const validProductTypes = ['home-insurance', 'auto-insurance', 'flood-insurance', 'life-insurance', 'medicare', 'commercial-insurance', 'recreational-insurance', 'umbrella-insurance'];
      
      if (!validProductTypes.includes(to.params.productType)) {
        next({ name: 'notFound' });
      } else {
        next();
      }
    }
  },
  
  { path: '/:pathMatch(.*)*', component: NotFoundPage, name: 'notFound' }, // Place notFound last
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// Log all available routes for debugging
// console.log('=== Router Configuration ===');
// console.log('All registered routes:');
// routes.forEach(route => {
//   console.log(`Route: ${route.path} => ${route.name || 'unnamed'}`);
//   if (route.redirect) console.log(`  (redirects to ${typeof route.redirect === 'function' ? 'function' : route.redirect})`);
// });

router.beforeEach((to, from, next) => {
  // Handle Chatwoot visibility
  if (to.meta.hideChatwoot) {
    // Add a small delay to ensure Chatwoot is loaded
    setTimeout(() => {
      if (window.hideChatwootWidget) {
        window.hideChatwootWidget();
      }
    }, 100);
  } else {
    setTimeout(() => {
      if (window.showChatwootWidget) {
        window.showChatwootWidget();
      }
    }, 100);
  }

  next();
});

export default router;
